import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  MultiStepFormProvider,
  SubmitHandlerWithDirtyFields,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { UpdateTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/tariff/update-tariff.model';
import { useMemo } from 'react';
import { Path, SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TariffUpdateFormStep1Component } from '@app/feature/tariff/tariff-update/tariff-update-form-step-1/tariff-update-form-step-1.component';
import { TariffUpdateFormStep2AlternateComponent } from '@app/feature/tariff/tariff-update/tariff-update-form-step-2-alternate/tariff-update-form-step-2-alternate.component';
import { TariffUpdateFormStep2Component } from '@app/feature/tariff/tariff-update/tariff-update-form-step-2/tariff-update-form-step-2.component';
import { TariffUpdateFormStep3Component } from '@app/feature/tariff/tariff-update/tariff-update-form-step-3/tariff-update-form-step-3.component';
import {
  useTariffUIContext,
  useUpdateTariffContext,
} from '@app/feature/tariff/tariff.context';

export interface TariffUpdateFormControllerProps {
  onError: SubmitErrorHandler<UpdateTariffModel>;
  onSubmit: SubmitHandlerWithDirtyFields<UpdateTariffModel>;
  onCancel: () => void;
  state: MultiStepFormState;
}

export const TariffUpdateFormController = (
  props: TariffUpdateFormControllerProps,
): React.JSX.Element => {
  const { onError, onSubmit, onCancel, state } = props;
  const { t } = useTranslation();
  const data = useUpdateTariffContext();
  const { isPriceRange } = useTariffUIContext();

  const items = useMemo(() => {
    if (isPriceRange) {
      return [
        {
          title: t('$.tariff-update.step-1.title'),
          iconName: 'info-circle',
          fields: [
            'tariff.name',
            'tariff.currency',
            'tariff.elements',
          ] as Path<UpdateTariffModel>[],
          component: <TariffUpdateFormStep1Component />,
        },
        {
          title: t('$.tariff-update.step-2.title'),
          iconName: 'info-circle',
          fields: ['tariff.elements'] as Path<UpdateTariffModel>[],
          component: <TariffUpdateFormStep2Component />,
        },
        {
          title: t('$.tariff-update.step-3.title'),
          iconName: 'info-circle',
          fields: ['tariff.elements'] as Path<UpdateTariffModel>[],
          component: <TariffUpdateFormStep3Component />,
        },
      ];
    }
    return [
      {
        title: t('$.tariff-update.step-1.title'),
        iconName: 'info-circle',
        fields: [
          'tariff.name',
          'tariff.currency',
          'tariff.elements',
        ] as Path<UpdateTariffModel>[],
        component: <TariffUpdateFormStep1Component />,
      },
      {
        title: t('$.tariff-update.step-2-alternate.title'),
        iconName: 'price',
        fields: ['tariff.elements'] as Path<UpdateTariffModel>[],
        component: <TariffUpdateFormStep2AlternateComponent />,
      },
    ];
  }, [t, isPriceRange]);

  return (
    <MultiStepFormProvider<UpdateTariffModel>
      useFormProps={{
        mode: 'all',
        defaultValues: data,
      }}
      submitErrorHandler={onError}
      submitHandler={onSubmit}
      onCancel={onCancel}
      state={state}
      items={items}
    />
  );
};
